import type { ComponentInstance, NodeRedFlow } from '@sapappgyver/appgyver-descriptors/lib/dolan/v4';
import { produce } from 'immer';
import { ComponentTemplate } from '../types';
import generateDeterministicComponentInstanceId from './generateDeterministicComponentInstanceId';
import migrateComponentInstanceLogicFlows from './migrateComponentInstanceLogicFlows';

/**
 * Ensures that the given Row component instance has enough cell children to match the number of cells
 * in its `columns` property. Any missing children are added as empty Container components.
 *
 * This takes the component instance to modify as well as available logic flows (can include excess logic).
 * Returns a new component instance with the necessary changes as well as the updated logic flows, e.g.
 * new logic canvases for new children.
 */
export default function addMissingRowComponentCells(
  viewId: number,
  componentInstance: ComponentInstance,
  flows: NodeRedFlow[],
): ComponentTemplate {
  const { name, args } = componentInstance;
  const { columns } = args;
  const targetChildrenCount = columns ?? 0;
  if (name !== 'layout' || typeof targetChildrenCount !== 'number') {
    // Not a Row component or invalid `columns` arg
    return { component: componentInstance, flows };
  }
  let updatedFlows = flows;
  const updatedComponentInstance = produce(componentInstance, (draftComponentInstance) => {
    // Add children as much as needed
    while (draftComponentInstance.children.length < targetChildrenCount) {
      // Create a new cell container
      const id = generateDeterministicComponentInstanceId(viewId, draftComponentInstance);
      const newContainerInstance: ComponentInstance = {
        id,
        args,
        name: 'vertical_box_layout',
        display_name: `Cell #${draftComponentInstance.children.length + 1}`,
        children: [],
        style: {
          gapAfter: 0,
          gapEnabled: true,
        },
        style_class: 'layout',
      };
      draftComponentInstance.children.push(newContainerInstance);
      updatedFlows = migrateComponentInstanceLogicFlows(updatedFlows, viewId, newContainerInstance);
    }
  });
  return { component: updatedComponentInstance, flows: updatedFlows };
}
